import React, { useEffect, useState } from 'react';
import { ArrowRight, Menu, X } from 'lucide-react';
import Logo from './Logo';

/**
 * Shared site header.
 *
 * Per-page customisation:
 *   - `navLinks`     anchor links shown in the center nav
 *   - `ctaHref`      destination of the primary CTA button
 *   - `ctaLabel`     label of the primary CTA button
 *   - `signInHref`   destination of the "Sign in" link
 *   - `logoHref`     destination when the logo is clicked
 */
export default function Navbar({
  navLinks = [],
  ctaHref = '#demo',
  ctaLabel = 'Book a demo',
  signInHref = 'https://app.projectapollo.io/login',
  logoHref = '#top',
}) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-40 w-full transition-all duration-200 ${
        scrolled
          ? 'bg-white/80 backdrop-blur-md border-b border-ink-200'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="container-page h-16 flex items-center justify-between">
        <a href={logoHref} className="shrink-0">
          <Logo />
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="px-3 py-2 text-sm font-medium text-ink-600 hover:text-ink-900 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a
            href={signInHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost text-sm"
          >
            Sign in
          </a>
          <a href={ctaHref} className="btn-primary text-sm">
            {ctaLabel}
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>

        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg text-ink-700 hover:bg-ink-100"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-ink-200 bg-white">
          <div className="container-page py-3 flex flex-col gap-1">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="px-3 py-2.5 text-sm font-medium text-ink-700 hover:bg-ink-100 rounded-lg"
              >
                {l.label}
              </a>
            ))}
            <div className="pt-2 mt-1 border-t border-ink-200 flex flex-col gap-2">
              <a
                href={signInHref}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary text-sm w-full"
              >
                Sign in
              </a>
              <a href={ctaHref} className="btn-primary text-sm w-full">
                {ctaLabel}
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
